/**
 * A lane's two-week series (J4): the daily median as a line, loads per day as
 * faint bars underneath.
 *
 * A NULL DAY IS A GAP, NEVER A ZERO. A day below the sample floor has no honest
 * median; drawing it at $0 (or interpolating across it) would invent a price
 * collapse. The line breaks there and the day's bar still shows its count,
 * because a count needs no floor.
 */

import Box from '@mui/joy/Box'
import Typography from '@mui/joy/Typography'
import type { MarketIntelLaneDay } from '@numeo/types-core'
import { money, totalMoney } from './marketIntel'

const W = 560
const H = 180
const PAD = { top: 12, right: 12, bottom: 22, left: 44 }

export function LaneSeriesChart({ days }: { days: MarketIntelLaneDay[] }) {
    if (days.length === 0) return <Typography level="body-sm">No daily series in this snapshot.</Typography>

    const rates = days.map((d) => d.p50).filter((v): v is number => v != null)
    const lo = rates.length ? Math.min(...rates) : 0
    const hi = rates.length ? Math.max(...rates) : 1
    const span = hi - lo || 1
    const yMin = Math.max(0, lo - span * 0.15)
    const yMax = hi + span * 0.15
    const maxObs = Math.max(1, ...days.map((d) => d.observations))

    const plotW = W - PAD.left - PAD.right
    const plotH = H - PAD.top - PAD.bottom
    const x = (i: number) => PAD.left + (days.length === 1 ? plotW / 2 : (i / (days.length - 1)) * plotW)
    const y = (v: number) => PAD.top + plotH - ((v - yMin) / (yMax - yMin)) * plotH
    const barW = Math.max(2, (plotW / days.length) * 0.5)

    // One path, restarted with `M` after every gap, so null days break the line.
    let path = ''
    let penDown = false
    days.forEach((d, i) => {
        if (d.p50 == null) {
            penDown = false
            return
        }
        path += `${penDown ? 'L' : 'M'}${x(i).toFixed(1)},${y(d.p50).toFixed(1)} `
        penDown = true
    })

    return (
        <Box
            component="svg"
            viewBox={`0 0 ${W} ${H}`}
            sx={{ width: '100%', maxWidth: W, height: 'auto', display: 'block' }}
            role="img"
            aria-label="Daily median $/mi"
        >
            {[yMin, (yMin + yMax) / 2, yMax].map((v) => (
                <g key={v}>
                    <line x1={PAD.left} x2={W - PAD.right} y1={y(v)} y2={y(v)} stroke="var(--joy-palette-neutral-outlinedBorder)" strokeDasharray="2 3" />
                    <text x={PAD.left - 6} y={y(v) + 4} textAnchor="end" fontSize="10" fill="var(--joy-palette-text-tertiary)">
                        {money(v)}
                    </text>
                </g>
            ))}
            {days.map((d, i) => {
                const h = (d.observations / maxObs) * plotH * 0.35
                return (
                    <rect key={`b${d.daysAgo}`} x={x(i) - barW / 2} y={PAD.top + plotH - h} width={barW} height={h} fill="var(--joy-palette-neutral-softBg)">
                        <title>
                            {d.daysAgo === 0 ? 'today' : `${d.daysAgo}d ago`}: {d.observations} loads · median {d.p50 == null ? 'below floor' : money(d.p50)} ·
                            total {d.totalP50 == null ? 'below floor' : totalMoney(d.totalP50)}
                        </title>
                    </rect>
                )
            })}
            <path d={path} fill="none" stroke="var(--joy-palette-primary-500)" strokeWidth={2} />
            {days.map((d, i) => (d.p50 == null ? null : <circle key={`p${d.daysAgo}`} cx={x(i)} cy={y(d.p50)} r={3} fill="var(--joy-palette-primary-500)" />))}
            {days.map((d, i) =>
                d.daysAgo % 2 === 0 ? (
                    <text key={`l${d.daysAgo}`} x={x(i)} y={H - 6} textAnchor="middle" fontSize="10" fill="var(--joy-palette-text-tertiary)">
                        {d.daysAgo === 0 ? 'today' : `-${d.daysAgo}d`}
                    </text>
                ) : null
            )}
        </Box>
    )
}
